'use client';

import { useEffect, useRef, useState } from 'react';
import useAlert from '@/hooks/useAlert';
import Button from '@/components/Button';
import { getStoredToken, setStoredSession } from '@/lib/apiFetch';
import { API_AUTH } from '@/lib/endpoints';

const COUNTRY_CODE = '968';

function Seal({ className = '' }) {
  return <img src="/images/logo-white.png" alt="Civil Defence and Ambulance Authority" className={`seal-in ${className}`} />;
}

function StepLabel({ step }) {
  return (
    <div className="mb-8">
      <p className="text-sm text-brand-700">Step {step} of 2</p>
      <div className="mt-2 flex gap-1">
        {[1, 2].map((n) => (
          <span key={n} className={`h-1 w-8 rounded-full ${n <= step ? 'bg-brand' : 'bg-brand-100'}`} />
        ))}
      </div>
    </div>
  );
}

export default function Login() {
  const timer = useRef();
  const [mobile, setMobile] = useState('');
  const [hasStartedVerification, setHasStartedVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const { setAlert } = useAlert();
  const [otpError, setOtpError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    if (getStoredToken()) {
      window.location.href = '/certificates';
    } else {
      setLoading(false);
    }

    return () => {
      clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    setDisabled(mobile === '');
  }, [mobile]);

  const sendOtp = async (e) => {
    e?.preventDefault();

    await fetch(API_AUTH, {
      method: 'POST',
      body: JSON.stringify({
        mobile: COUNTRY_CODE + mobile,
      }),
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => {
        response
          .json()
          .then((data) => {
            if (data.data) {
              setAlert(data.message, 'success');
              setHasStartedVerification(true);
            } else if (data.error) {
              setAlert(data.error, 'error');
            }
          })
          .catch((err) => {
            setAlert(err.error, 'error');
          });
      });
  };

  const verifyOtp = async () => {
    await fetch(API_AUTH, {
      method: 'PUT',
      body: JSON.stringify({
        mobile: COUNTRY_CODE + mobile,
        verificationCode: verificationCode,
      }),
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => {
        response
          .json()
          .then((data) => {
            if (data.token) {
              setStoredSession(data.token, data.user);
              window.location.href = '/certificates';
            } else if (data.error) {
              if (data.error == 'Otp invalid') {
                setOtpError(true);
              }
              setAlert(data.error, 'error');
            }
          })
          .catch(() => {});
      });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
      <style jsx>{`
        .seal-in {
          animation: seal-in 900ms ease-out;
        }
        @keyframes seal-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .seal-in {
            animation: none;
          }
        }
      `}</style>

      <div className="hidden flex-col items-center justify-center gap-10 bg-brand-900 px-12 py-16 lg:flex">
        <Seal className="w-64" />
        <div className="text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-white">Civil Defence and Ambulance Authority</h1>
          <p className="mx-auto mt-2 max-w-xs text-sm text-brand-200">
            Issue and approve Royal Oman Police training-completion certificates. Verify any
            certificate's authenticity in seconds.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-paper px-6 py-16 sm:px-12">
        <div className="w-full max-w-sm">
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 shrink-0 items-center rounded-md bg-brand-900 px-2.5">
              <Seal className="h-6 w-auto" />
            </div>
            <span className="text-lg font-semibold text-brand-900">Civil Defence and Ambulance Authority</span>
          </div>

          {!hasStartedVerification ? (
            <form onSubmit={sendOtp}>
              <StepLabel step={1} />
              <h2 className="mb-1 text-2xl font-semibold text-brand-900">Sign in</h2>
              <p className="mb-6 text-sm text-brand-700">Enter your mobile number to receive a one-time code.</p>
              <div className="flex flex-col gap-6">
                <label className="block">
                  <span className="text-sm text-brand-900">Mobile number</span>
                  <div className="mt-1.5 flex items-center gap-2 border-b border-brand-200 pb-2 focus-within:border-brand">
                    <span className="shrink-0 border-r border-brand-200 pr-2 text-sm font-medium text-brand-700">+968</span>
                    <input
                      id="mobile"
                      name="user-mobile"
                      type="tel"
                      inputMode="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      autoComplete="tel"
                      maxLength={8}
                      className="w-full bg-transparent text-brand-900 outline-none"
                    />
                  </div>
                </label>
                <Button
                  disabled={disabled}
                  onClick={sendOtp}
                  className="mt-2 rounded-md bg-brand px-4 py-2.5 font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Continue
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={(e) => e.preventDefault()}>
              <StepLabel step={2} />
              <h2 className="mb-2 text-2xl font-semibold text-brand-900">Enter the code</h2>
              <p className="mb-6 text-sm text-brand-700">We sent a one-time code to your registered mobile number.</p>
              <div className="flex flex-col gap-6">
                <label className="block">
                  <span className="text-sm text-brand-900">One-time code</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="otp"
                    id="otp"
                    autoComplete="off"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="mt-1.5 w-full border-b border-brand-200 bg-transparent pb-2 text-brand-900 outline-none focus:border-brand"
                  />
                </label>
                <Button
                  onClick={verifyOtp}
                  className="rounded-md bg-brand px-4 py-2.5 font-medium text-white hover:bg-brand-600"
                >
                  Verify code
                </Button>
                {otpError && (
                  <Button
                    onClick={sendOtp}
                    className="text-sm font-medium text-brand-700 hover:text-brand underline underline-offset-2"
                  >
                    Resend code
                  </Button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
